import { useState, useEffect } from "react";
import { Wallet, Check, X } from "lucide-react";
import { formatEuro } from "@/lib/api";

/**
 * StartCashDialog — appears at the start of each cashier shift.
 * Confirms how much Wechselgeld (start cash) is in the till today.
 *
 * Props:
 *  - open: bool
 *  - defaultAmount: number (pre-filled, e.g. last value)
 *  - showTitle, showDate: strings (header context)
 *  - dismissible: if true, allow Close button (when called from edit-pencil after confirmation)
 *  - onConfirm({ amount }): async fn returning the updated show
 *  - onClose: dismiss without saving (only when dismissible)
 */
export default function StartCashDialog({
  open,
  defaultAmount = 1150,
  showTitle = "",
  showDate = "",
  dismissible = false,
  onConfirm,
  onClose,
}) {
  const [amount, setAmount] = useState(String(defaultAmount));
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setAmount(String(defaultAmount));
      setErr("");
    }
  }, [open, defaultAmount]);

  if (!open) return null;

  const parsed = parseFloat((amount || "0").replace(",", "."));
  const valid = !isNaN(parsed) && parsed >= 0;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!valid) { setErr("Bitte gültige Zahl eingeben (z.B. 200 oder 200,50)."); return; }
    setSubmitting(true); setErr("");
    try {
      await onConfirm({ amount: parsed });
    } catch (e) {
      setErr(e?.message || "Konnte nicht speichern. Bitte erneut versuchen.");
    }
    setSubmitting(false);
  };

  const presets = [500, 800, 1000, 1150, 1500, 2000];

  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-950/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={dismissible ? onClose : undefined}
      data-testid="startcash-dialog"
    >
      <form onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-xl w-full">
        <div className="px-8 pt-8 pb-2 flex items-start gap-4">
          <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <Wallet className="h-8 w-8 text-emerald-700" />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-500">
              {dismissible ? "Wechselgeld anpassen" : "Bevor du startest"}
            </p>
            <h2 className="text-3xl font-black tracking-tight">Wieviel Wechselgeld?</h2>
            {showTitle && (
              <p className="text-zinc-500 text-base mt-1">{showTitle} · {showDate}</p>
            )}
          </div>
          {dismissible && (
            <button type="button" onClick={onClose}
              className="h-10 w-10 rounded-full hover:bg-zinc-100 flex items-center justify-center"
              data-testid="startcash-close">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="px-8 py-5">
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Trage hier ein, wie viel Bargeld du heute zum Start in der Kassa hast.
            Das ist wichtig für die Rückgeld-Berechnung und den Tagesabschluss.
          </p>

          <label className="text-sm uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">
            Betrag in € *
          </label>
          <input
            data-testid="startcash-input"
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            autoFocus
            className="w-full h-20 px-5 text-5xl font-mono font-black tabular-nums border-2 border-zinc-300 rounded-xl focus:outline-none focus:border-emerald-600 text-center"
          />

          <p className="text-sm text-zinc-500 mt-2 mb-4">Oder schnell auswählen:</p>
          <div className="grid grid-cols-3 gap-3">
            {presets.map((v) => (
              <button key={v} type="button"
                data-testid={`startcash-preset-${v}`}
                onClick={() => setAmount(String(v))}
                className={`h-14 rounded-xl border-2 font-black text-lg ${
                  parsed === v
                    ? "border-emerald-600 bg-emerald-50 text-emerald-900"
                    : "border-zinc-200 bg-white hover:bg-zinc-50"
                }`}>
                {formatEuro(v)}
              </button>
            ))}
          </div>

          {err && (
            <div className="mt-4 text-base font-bold text-red-800 bg-red-50 border-2 border-red-300 rounded-xl px-4 py-3" role="alert">
              {err}
            </div>
          )}
        </div>

        <div className="px-8 py-6 border-t-2 border-zinc-100 flex gap-3">
          {dismissible && (
            <button type="button" onClick={onClose}
              className="flex-1 h-16 rounded-xl border-2 border-zinc-200 hover:bg-zinc-50 font-bold text-base">
              Abbrechen
            </button>
          )}
          <button
            type="submit"
            data-testid="startcash-confirm"
            disabled={!valid || submitting}
            className="flex-1 h-16 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black text-lg flex items-center justify-center gap-2">
            <Check className="h-6 w-6" />
            {submitting ? "Speichern…" : (dismissible ? "Übernehmen" : "Bestätigen & weiter")}
          </button>
        </div>
      </form>
    </div>
  );
}
